import {classNames, concatenate} from "../../../services/string";
import React from "react";
import EventItem from "./EventItem";
import {DateTime, Interval} from "luxon";
import {Event} from 'types/Event'
import ChevronLeftIcon from "@heroicons/react/outline/ChevronLeftIcon";
import {ChevronRightIcon} from "@heroicons/react/outline";
import {CalendarView} from "./EventSchedule";

// Types ----

type DayViewProps = {
  selectedDate: DateTime
  selectDate: (date: DateTime) => void
  events: Array<Event> | undefined
  isLoading: boolean
  onDeleteEvent: (event: Event) => void
  calendarView: CalendarView
}

// Constants ----

export const FIVE_MINUTE_SLOTS = 288
export const HALF_HOUR_SLOTS = 48

// Helpers ----

function isToday(date: DateTime): boolean {
  return DateTime.now().startOf('day').equals(date.startOf('day'))
}

function daysOfWeek(date?: DateTime): DateTime[] {
  const referenceDate = date || DateTime.now()
  const startOfWeek = referenceDate.startOf('week')
  const days = []

  for (let i = 0; i < 7; i++) {
    days.push(startOfWeek.plus({days: i}))
  }

  return days
}

function hoursOfDay(): DateTime[] {
  const time = DateTime.now().startOf('day')
  const hours = []

  for (let i = 0; i < 24; i++) {
    hours.push(time.plus({hours: i}))
  }

  return hours
}

function createMiniCalendarItems(date: DateTime) {
  // Get the start and end date including days from the previous month and next month
  const startDate = date.startOf('month').startOf('week')
  const endDate = date.endOf('month').endOf('week')


  // Create an interval from the start and end dates in order to generate each day in the calendar date range
  return Interval.fromDateTimes(startDate, endDate).splitBy({days: 1})
}

// Component ----

function DayView({selectedDate, selectDate, events, isLoading, onDeleteEvent, calendarView}: DayViewProps) {
  const miniCalendarItems = createMiniCalendarItems(selectedDate)

  return (
    <div className="isolate flex flex-auto overflow-auto bg-white h-[calc(100vh_-_16rem)] md:h-[calc(100vh_-_14rem)]">
      <div
        className="relative flex flex-auto flex-col overflow-auto bg-white shadow scrollbar-thin scrollbar-track-rounded-lg scrollbar-track-gray-200 scrollbar-thumb-rounded-lg scrollbar-thumb-gray-400">
        {/* Day Headers */}
        <div className="sticky top-0 z-10 flex-none bg-white shadow ring-1 ring-black ring-opacity-5 sm:pr-8">
          {/* Mobile Day Headers */}
          <div className="grid grid-cols-7 text-sm leading-6 text-gray-500 sm:hidden">
            {daysOfWeek(selectedDate).map(date => (
              <button
                type="button"
                className="flex flex-col items-center pt-2 pb-3"
                key={date.day}
                onClick={() => selectDate(date)}
              >
                {date.weekdayShort.substring(0, 1)}{' '}
                <span className={concatenate(
                  isToday(date) ? 'bg-brand hover:bg-brand/20 text-white rounded-full' : (date.equals(selectedDate) ? ' bg-gray-300 hover:bg-gray-200 text-gray-900 rounded-full' : 'text-gray-900'),
                  'mt-1 flex h-8 w-8 items-center justify-center font-semibold')}
                >
                    {date.day}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex w-full flex-auto relative">
          <div className="sticky left-0 w-14 flex-none bg-white ring-1 ring-gray-100"/>

          <div className="grid flex-auto grid-cols-1 grid-rows-1">
            {/* Horizontal lines */}
            <div
              className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
              style={{gridTemplateRows: `repeat(${HALF_HOUR_SLOTS}, minmax(3.5rem, 1fr))`}}>

              {/* Current time indicator */}
              <div className="row-end-1 h-7"/>

              {/* Hours of day */}
              {hoursOfDay().map(time => (
                <React.Fragment key={time.hour}>
                  <div>
                    <div
                      className="sticky left-0 -mt-2.5 -ml-14 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                      {time.toFormat('ha')}
                    </div>
                  </div>
                  <div/>
                </React.Fragment>
              ))}
            </div>

            {/* Events */}
            <ol
              className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:pr-8 auto-cols-fr"
              style={{gridTemplateRows: `1.75rem repeat(${FIVE_MINUTE_SLOTS}, minmax(0, 1fr)) auto`}}
            >
              {events?.map(event => (
                <EventItem
                  event={event}
                  eventList={events}
                  selectedDate={selectedDate}
                  calendarView={calendarView}
                  onDeleteEvent={onDeleteEvent}
                  key={event.id}
                />
              ))}
            </ol>

          </div>

          {/* Loading overlay */}
          {isLoading && <div className='absolute bg-white inset-0 flex items-center justify-center opacity-50'/>}
        </div>
      </div>

      {/* Mini calendar */}
      <div className="hidden w-1/2 max-w-md flex-none border-l border-gray-100 px-8 py-10 md:block">
        <div className="flex items-center text-center text-gray-900">
          <button
            type="button"
            onClick={() => selectDate(selectedDate.minus({month: 1}))}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Previous month</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true"/>
          </button>
          <div className="flex-auto text-sm font-semibold">{selectedDate.toFormat('LLLL yyyy')}</div>
          <button
            type="button"
            onClick={() => selectDate(selectedDate.plus({month: 1}))}
            className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Next month</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true"/>
          </button>
        </div>
        <div className="mt-6 grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
          <div>M</div>
          <div>T</div>
          <div>W</div>
          <div>T</div>
          <div>F</div>
          <div>S</div>
          <div>S</div>
        </div>
        <div
          className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
          {miniCalendarItems.map((date, dayIdx) => {
            const IS_SELECTED = (selectedDate?.day === date.start.day) && (selectedDate?.month === date.start.month)
            const IS_TODAY = isToday(date.start)
            const IS_CURRENT_MONTH = date.start.month === selectedDate.month

            return (
              <button
                key={crypto.randomUUID()}
                type="button"
                onClick={() => selectDate(date.start)}
                className={classNames(
                  'py-1.5 hover:bg-gray-100 focus:z-10',
                  IS_CURRENT_MONTH ? 'bg-white' : 'bg-gray-50',
                  (IS_SELECTED || isToday(date.start)) && 'font-semibold',
                  IS_SELECTED && 'text-white',
                  !IS_SELECTED && IS_CURRENT_MONTH && !IS_TODAY && 'text-gray-900',
                  !IS_SELECTED && !IS_CURRENT_MONTH && !IS_TODAY && 'text-gray-400',
                  IS_TODAY && !IS_SELECTED && 'text-brand',
                  dayIdx === 0 && 'rounded-tl-lg',
                  dayIdx === 6 && 'rounded-tr-lg',
                  dayIdx === miniCalendarItems.length - 7 && 'rounded-bl-lg',
                  dayIdx === miniCalendarItems.length - 1 && 'rounded-br-lg'
                )}
              >
                <time
                  dateTime={date.start.toISO()}
                  className={classNames(
                    'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
                    IS_SELECTED && IS_TODAY && 'bg-brand',
                    IS_SELECTED && !IS_TODAY && 'bg-gray-900'
                  )}
                >
                  {date.start.toISODate().split('-').pop()?.replace(/^0/, '')}
                </time>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default DayView